import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Divider } from '#components/atoms/divider/Divider.tsx';
import { HelpTip } from '#components/atoms/forms/help-tip/HelpTip.tsx';
import { ErrorMessage, Input, Label } from '#components/atoms/forms/index.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { ComboboxTags } from '#components/patterns/forms/select/combobox/combobox-tags/ComboboxTags.tsx';
import { SelectAsyncControlled } from '#components/patterns/forms/select/select-async/SelectAsyncControlled.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { SelectControlled } from '#components/patterns/forms/select/select/SelectControlled.tsx';
import { NetworkSize } from '#components/patterns/network/NetworkSize.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import { cidrRequiredSchema } from '#schemas/general/cidr.ts';
import { rolesForSelectionQueryOptions } from '#screens/roles/detail/firewall-rule-form/FirewallRuleForm.queries.ts';
import { PortsField } from '#screens/roles/detail/firewall-rule-form/ports-field/PortsField.tsx';
import { cidrFirewallRuleFormSchema, type CIDRFirewallRuleFormData } from './cidr-firewall-rule-form-schema.ts';

const PROTOCOL_LABELS = { TCP: 'TCP', UDP: 'UDP', ICMP: 'ICMP', ANY: 'Any' };
const PROTOCOLS = ['ANY', 'TCP', 'UDP', 'ICMP'] as const;

type Props = {
  rule: CIDRFirewallRuleFormData;
  onSave: (values: CIDRFirewallRuleFormData) => void;
};

// TODO: This was copy/pasted from roles.  We should extract schemas, and share some of
// the parts of the form, if not the entire form itself.
export function CIDRFirewallRuleForm({ rule: defaultValues, onSave }: Props) {
  const formContext = useForm({
    defaultValues,
    resolver: zodResolver(cidrFirewallRuleFormSchema),
  });
  const { register, handleSubmit, formState, control } = formContext;
  const { errors } = formState;
  const [watchedProtoValue, currentCIDR] = useWatch({ control, name: ['protocol', 'localCIDR'] });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    return handleSubmit((data) => {
      return Promise.resolve(onSave(data));
    })(e);
  };

  return (
    <FormProvider {...formContext}>
      <form method="post" id="cidr-firewall-rule-form" onSubmit={onSubmit}>
        <Spacer direction="vertical" size="wide">
          <Field label="Description" className="width-100">
            <Input
              className="width-100"
              maxLength={255}
              hasError={Boolean(errors.description)}
              {...register('description')}
            />
            {errors.description && <ErrorMessage>{errors.description.message}</ErrorMessage>}
          </Field>
          <Field
            id="local-cidr"
            label={
              <Label htmlFor="local-cidr">
                CIDR<HelpTip portal={false}>To which destination IP addresses should this rule apply?</HelpTip>
              </Label>
            }
          >
            <Input className="width-100" {...register('localCIDR')} />
            {errors.localCIDR ?
              <ErrorMessage>{errors.localCIDR.message}</ErrorMessage>
            : cidrRequiredSchema.safeParse(currentCIDR).success ?
              <NetworkSize cidr={currentCIDR} reservedAddressCount={0} />
            : null}
          </Field>
          <Spacer size="wide" stackWhenXNarrow={true}>
            <SelectControlled
              className="width-100"
              control={control}
              name="protocol"
              label="Protocol"
              placeholder="Select a protocol…"
              errorMessage={errors.protocol?.message}
              renderValue={(item: keyof typeof PROTOCOL_LABELS) => PROTOCOL_LABELS[item]}
              renderOptions={({ onChange }) => (
                <>
                  {PROTOCOLS.map((proto) => (
                    <SelectItem key={proto} value={proto} onClick={() => onChange(proto)}>
                      {PROTOCOL_LABELS[proto]}
                    </SelectItem>
                  ))}
                </>
              )}
            />
            <PortsField className="width-100" disabled={watchedProtoValue === 'ICMP'} />
          </Spacer>
          <div>
            <h3 className="h5 mt-0 mb-16">From hosts matching</h3>
            <SelectAsyncControlled
              className="width-100 mb-16"
              getItemsQueryOptions={rolesForSelectionQueryOptions}
              control={control}
              name="allowedRoleID"
              label="Role"
              placeholder="Select a role…"
              errorMessage={errors.allowedRoleID?.message}
              renderOptions={({ onChange, items }) => (
                <>
                  {items.map((itm) => (
                    <SelectItem key={itm.value} value={itm.value} onClick={() => onChange(itm.value)}>
                      {itm.label}
                    </SelectItem>
                  ))}
                </>
              )}
            />
            <Divider className="mb-12">AND</Divider>
            <ComboboxTags name="tags" label="Tags" control={control} unknownTags="reference" />
          </div>
        </Spacer>
      </form>
    </FormProvider>
  );
}
